/* eslint-disable react/no-array-index-key */
import React, { useRef, useId } from 'react';
import PropTypes from 'prop-types';
import classnamesBind from 'classnames/bind';
import NavMenu from '@concur/nui-widgets/NavMenu';
import Separator from '@concur/nui-widgets/Separator';
import useAutoOverflow from '@concur/nui-widgets/lib/utils/useAutoOverflow';
import { withFormatter } from '@concur/nui-intl-runtime';
import { compose } from '@concur/core-ui-shell';
import keycode from 'keycode';
import SideNavItem from './_SideNavItem';
import SideNavItemMenu from './_SideNavItemMenu';
import convertJavascriptUrl from '../utils/convertJavascriptUrl';
import styles from './SideNav.css';
import { getSidenavItemAnalyticsJson } from '../utils/getSidenavItemAnalyticsJson';
import { triggerSidenavItemAnalyticsClick } from '../utils/triggerSidenavItemAnalyticsClick';

const CSS_BLOCK = 'sapcnqr-sidenav';
const CSS_PREFIX = 'sapcnqr';

const SideNav = (props) => {
    const {
        className,
        menuItems,
        formatter,
        activePathArray,
    } = props;

    const {
        homeMenuItem,
        applicationMenuItems,
        adminMenuItem,
        adminMenuItems,
        linksMenuItem,
        testDriveMenuItem,
        subscribeMenuItem,
    } = menuItems;

    const classnames = classnamesBind.bind(styles);

    const targetRef = useRef(null);

    const classes = classnames(
        className,
        CSS_PREFIX,
        CSS_BLOCK,
    );

    const listClasses = classnames(
        CSS_PREFIX,
        `${CSS_BLOCK}__list`,
    );

    const getListItemClasses = (item, hasSubItems) => classnames(
        CSS_PREFIX,
        `${CSS_BLOCK}__list-item`,
        {
            [`${CSS_BLOCK}__list-item--menu`]: !!hasSubItems,
            [`${CSS_BLOCK}__list-item--active`]: item?.isActive || activePathArray?.includes(item.id),
        },
    );

    const itemTextClasses = classnames(
        CSS_PREFIX,
        `${CSS_BLOCK}__item-text`,
    );

    const subMenuItemText = classnames(
        CSS_PREFIX,
        `${CSS_BLOCK}__sub-item-text`,
    );

    const getItemClasses = (hasIcon) => classnames(
        CSS_PREFIX,
        `${CSS_BLOCK}__item`,
        {
            [`${CSS_BLOCK}__item--icon`]: !!hasIcon,
        },
    );

    const getSubItemClasses = (item) => classnames(
        CSS_PREFIX,
        `${CSS_BLOCK}__subitem`,
        {
            [`${CSS_BLOCK}__subitem--active`]: item?.isActive || activePathArray?.includes(item.id),
        },
    );

    const renderSubMenuItems = (items, level) => (
        items.map((subItem, index) => {
            if (subItem.subItems) {
                const buttonId = useId();
                const hasActiveSubItem = activePathArray?.includes(subItem.id);
                const adjustedSubItem = {
                    ...subItem,
                    isActive: subItem.isActive || hasActiveSubItem,
                };

                return (
                    <NavMenu.Item
                        label={subItem.name}
                        buttonProps={{
                            className: getSubItemClasses(adjustedSubItem),
                            innerTextClasses: subMenuItemText,
                            id: buttonId,
                            'data-test': `sidenav__subitem__button-${subItem.id}`,
                            innerProps: {
                                className: classnames(CSS_PREFIX, `${CSS_BLOCK}__subbutton`, `${CSS_BLOCK}__subbutton--${level}`),
                            },
                            'data-analytics-json': getSidenavItemAnalyticsJson(subItem.id),
                        }}
                        submenuListProps={{
                            className: classnames(CSS_PREFIX, `${CSS_BLOCK}__container`),
                        }}
                        overlayProps={{
                            className: classnames(CSS_PREFIX, `${CSS_BLOCK}__overlay`),
                        }}
                        ariaCurrent={subItem.isActive}
                        linkProps={{
                            className: getSubItemClasses(adjustedSubItem),
                            innerTextClasses: subMenuItemText,
                            'data-test': `sidenav__subitem__link-${subItem.id}`,
                        }}
                        iconProps={{
                            iconName: 'navigation-right-arrow',
                        }}
                        key={`navitem_${level}_${index}`}
                        tooltipMessage={formatter.formattedMessage({ id: 'CoreUI.openSubMenu', values: { applicationName: subItem.name } })}
                        tooltipProps={{
                            outerPopperProps: {
                                showArrow: false,
                                offset: 3,
                            },
                            innerContainerProps: {
                                className: classnames(`${CSS_BLOCK}__tooltip-inner`),
                            },
                            delay: 1000,
                        }}
                        onKeyDown={(e) => {
                            if (e.keyCode === keycode.codes.enter
                                 || e.keyCode === keycode.codes.space
                                  || e.keyCode === keycode.codes.right) {
                                triggerSidenavItemAnalyticsClick(subItem.id);
                            }
                        }}
                    >
                        {renderSubMenuItems(subItem.subItems, level + 1)}
                    </NavMenu.Item>
                );
            }
            if (subItem.url) {
                return (
                    <NavMenu.Item
                        {...convertJavascriptUrl(subItem.url, subItem.id)}
                        label={subItem.name}
                        ariaCurrent={subItem.isActive ? 'page' : false}
                        linkProps={{
                            target: subItem?.target || 'self',
                            className: getSubItemClasses(subItem),
                            innerTextClasses: subMenuItemText,
                            'data-test': `sidenav__subitem__link-${subItem.id}`,
                            'data-analytics-json': getSidenavItemAnalyticsJson(subItem.id),
                        }}
                        key={`navitem_${level}_${index}`}
                    />
                );
            }
            return null;
        }));

    const getNavChildren = (item, index) => {
        if (item?.subItems) {
            const buttonId = useId();
            const hasActiveSubItem = activePathArray?.includes(item.id);
            const adjustedItem = {
                ...item,
                isActive: item.isActive || hasActiveSubItem,
            };

            // only add active class  to duplicate group header link entry
            // if there are no active subitems
            // otherwise there will be 2 list items highlighted
            const isActive = item?.isActive && !hasActiveSubItem;

            // menuData from Menu.cshtml will have urls of "#" sometimes in place of being empty
            // we do not want these to be considered urls for NavMenu.Group headers
            const hasValidUrl = item.url && item.url !== '#';

            return (
                <SideNavItemMenu
                    item={adjustedItem}
                    // not allowing menus to overflow - having issues with getting the styling/logic
                    // right when they are moved to overflow menu
                    data-no-overflow
                    data-test={`sidenav__listitem-${item.id}`}
                    iconProps={{
                        iconName: 'navigation-right-arrow',
                        className: classnames(CSS_PREFIX, `${CSS_BLOCK}__icon`),
                        'data-test': `sidenav__icon-${item.id}`,
                    }}
                    buttonProps={{
                        innerProps: {
                            className: getItemClasses(true),
                        },
                        className: getListItemClasses(adjustedItem, true),
                        innerTextClasses: itemTextClasses,
                        id: buttonId,
                        'data-test': `sidenav__button-${item.id}`,
                        'data-analytics-json': getSidenavItemAnalyticsJson(item.id),
                    }}
                    className={classnames(CSS_PREFIX, `${CSS_BLOCK}__navmenu`)}
                    key={`navitem_1_${index}`}
                    menuAriaLabel={item.name}
                    overlayProps={{
                        showArrow: true,
                        offset: 5,
                        popperPlacement: ['right-start', 'right-end', 'right', 'bottom-start', 'bottom-end', 'top-start', 'top-end'],
                    }}
                    onKeyDown={(e) => {
                        if (e.keyCode === keycode.codes.enter
                            || e.keyCode === keycode.codes.space
                            || e.keyCode === keycode.codes.right) {
                            triggerSidenavItemAnalyticsClick(item.id);
                        }
                    }}
                >
                    {/* Only using NavMenu.Group headers when an
                    item with subitems does not have a valid url */}
                    <NavMenu.Group
                        header={!hasValidUrl ? item.name : undefined}
                        headerProps={{
                            className: classnames(CSS_PREFIX, `${CSS_BLOCK}__header`),
                        }}
                    >
                        {/* duplicate item for group header when valid url is provided */}
                        {hasValidUrl && (
                            <NavMenu.Item
                                label={item.name}
                                ariaCurrent={isActive ? 'page' : false}
                                linkProps={{
                                    className: classnames(
                                        CSS_PREFIX,
                                        `${CSS_BLOCK}__subitem`,
                                        `${CSS_BLOCK}__subitem--header`,
                                        {
                                            [`${CSS_BLOCK}__subitem--active`]: isActive,
                                        },
                                    ),
                                    innerTextClasses: subMenuItemText,
                                    'data-test': `sidenav__subitem__link-${item.id}`,
                                }}
                                href={item.url}
                                data-analytics-json={getSidenavItemAnalyticsJson(item.id)}
                            />
                        )}
                        {renderSubMenuItems(item.subItems, 2)}
                    </NavMenu.Group>
                </SideNavItemMenu>
            );
        }

        let additionalProps = {};
        if (item.isActive || activePathArray?.includes(item.id) || index === 'home') {
            // active top level items will not overflow
            additionalProps = {
                'data-no-overflow': true,
            };
        }

        return (
            <SideNavItem
                data-test={`sidenav__listitem-${item.id}`}
                {...additionalProps}
                className={getListItemClasses(item)}
                key={`navitem_1_${index}`}
                ariaCurrent={item.isActive ? 'page' : false}
                linkProps={{
                    className: getItemClasses(false),
                    innerTextClasses: itemTextClasses,
                    'data-test': `sidenav__link-${item.id}`,
                }}
                href={item.url}
                item={item}
                formatter={formatter}
                data-analytics-json={getSidenavItemAnalyticsJson(item.id)}
            />
        );
    };

    // Phase 2 (SMNUI-20048): Prefer adminMenuItems array (top-level items)
    // over adminMenuItem (collapsible parent). Falls back when absent or empty.
    const hasAdminMenuItems = Array.isArray(adminMenuItems)
        && adminMenuItems.length > 0;

    let adminChildren = [];
    if (hasAdminMenuItems) {
        adminChildren = adminMenuItems.map(
            (item, i) => getNavChildren(item, `admin${i}`),
        );
    } else if (adminMenuItem && adminMenuItem.subItems) {
        adminChildren = [getNavChildren(adminMenuItem, 'Administration')];
    }

    const { isOverflowing, childrenToRender, overflowChildrenToRender } = useAutoOverflow({
        targetRef,
        // for useAutoOverflow to work properly, children needs to be an array
        children:
            [
                homeMenuItem ? getNavChildren(homeMenuItem, 'home') : [],
                testDriveMenuItem ? getNavChildren(testDriveMenuItem, 'testDrive') : [],
                subscribeMenuItem ? getNavChildren(subscribeMenuItem, 'subscribe') : [],
                applicationMenuItems ? (

                    applicationMenuItems.map((item, i) => getNavChildren(item, i))
                ) : [],
                (adminMenuItem || adminMenuItems || linksMenuItem) ? (
                    <Separator className={classnames(CSS_PREFIX, `${CSS_BLOCK}__separator`)} />
                ) : [],
                ...adminChildren,
                linksMenuItem ? linksMenuItem.subItems && getNavChildren(linksMenuItem, 'Favorites') : [],
            ],
        overflowPadding: 44,
        orientation: 'vertical',
        observedDimension: 'height',
        debounce: 1000,
    });

    const overflowMenuItem = {
        name: formatter.formattedMessage({ id: 'CoreUIComponents.more' }),
        id: 'overflow',
        isActive: false,
    };

    return (
        <NavMenu.Bar
            ariaLabel={formatter.formattedMessage({ id: 'CoreUI.sideNavAriaLabel' })}
            className={classes}
            listProps={{
                className: listClasses,
                ref: targetRef,
            }}
            orientation="vertical"
            id="sideNavBar"
            data-test="sidenavbar"
        >
            {childrenToRender}
            {isOverflowing && (
                <SideNavItemMenu
                    item={overflowMenuItem}
                    iconProps={{
                        iconName: 'navigation-right-arrow',
                        className: classnames(CSS_PREFIX, `${CSS_BLOCK}__icon`),
                    }}
                    buttonProps={{
                        innerProps: {
                            className: getItemClasses(true),
                        },
                        className: getListItemClasses(overflowMenuItem, true),
                        innerTextClasses: itemTextClasses,
                    }}
                    className={classnames(CSS_PREFIX, `${CSS_BLOCK}__navmenu`)}
                    key="navitem_overflow"
                    menuAriaLabel={overflowMenuItem.name}
                    overlayProps={{
                        showArrow: true,
                        offset: 5,
                        popperPlacement: ['right-start', 'right-end', 'right', 'bottom-start', 'bottom-end', 'top-start', 'top-end'],
                    }}
                >
                    {React.Children.map(overflowChildrenToRender, (child) => {
                        if (child === null) return null;
                        // remove separators from overflow menu
                        if (child.type.displayName === 'Separator') return null;
                        return React.cloneElement(child, {
                            inOverflowMenu: true,
                            linkProps: {
                                className: getSubItemClasses(child.props.item),
                                innerTextClasses: subMenuItemText,
                            },
                        });
                    })}
                </SideNavItemMenu>
            )}
        </NavMenu.Bar>
    );
};

SideNav.displayName = 'SideNav';

SideNav.propTypes = {
    menuItems: PropTypes.object.isRequired,
    className: PropTypes.string,
    activePathArray: PropTypes.arrayOf(PropTypes.string),
};

export default compose(
    withFormatter,
)(SideNav);
